import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AirportSelect from '../../components/AirportSelect/AirportSelect';
import useFlightSearch from '../../hooks/useFlightSearch';
import apiClient from '../../services/apiClient';
import toast from 'react-hot-toast';
import departureIcon from '../../assets/images/departure.svg';
import arrivalIcon from '../../assets/images/arrival.svg';
import dateTimeIcon from '../../assets/images/date-time.svg';
import passengersIcon from '../../assets/images/pessangers.svg';
import {
  getPartialChangeContext,
  parseChangeLegTokensFromQuery,
} from '../../utils/partialLegChange';
import { isPjoRejectionRebookFlow } from '../../utils/pjoRejection';
import SEOHead from '../../components/SEOHead/SEOHead';

const SearchFlightPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchFlights } = useFlightSearch();
  const { user } = useSelector((state) => state.auth);

  const [tripType, setTripType] = useState('One Way');
  const [fromAirport, setFromAirport] = useState('');
  const [toAirport, setToAirport] = useState('');
  const [passengers, setPassengers] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [addNearbyAirports, setAddNearbyAirports] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [bookingToken, setBookingToken] = useState('');
  const [changeLegs, setChangeLegs] = useState([]);
  const [originalBookingFlights, setOriginalBookingFlights] = useState([]);
  const [originalRouteType, setOriginalRouteType] = useState(null);
  const [isLoadingBooking, setIsLoadingBooking] = useState(false);

  const isChangeFlight = !!(bookingToken || user?.change_flight);
  const partialChange = getPartialChangeContext(originalBookingFlights, changeLegs);
  const isPartialReturnChange =
    isChangeFlight && originalRouteType === 'return' && partialChange.isPartialChange;
  const isPartialMulticityChange =
    isChangeFlight && originalRouteType === 'multi_city' && partialChange.isPartialChange;

  const lockPassengers = isChangeFlight;
  // Lock airports only for partial leg change — changing all legs allows new routes
  const lockAirports = isChangeFlight && partialChange.isPartialChange;
  const lockTripTypeSwitch = isChangeFlight && originalRouteType === 'return';
  const lockDepartureDate =
    isPartialReturnChange && !partialChange.changingOutbound;
  const lockReturnDate =
    isPartialReturnChange && !partialChange.changingReturn;

  const isMulticitySegmentLocked = (segmentIndex) =>
    isPartialMulticityChange &&
    !partialChange.editableSegmentIndices.includes(segmentIndex);

  // Multicity state - support up to 5 segments
  const [multicitySegments, setMulticitySegments] = useState([
    { id: 1, from: '', to: '', date: '' },
    { id: 2, from: '', to: '', date: '' },
  ]);

  // Fetch booking details and prefill the form for rebooking
  const fetchAndPrefillBooking = async (token, legTokensOverride = null) => {
    setIsLoadingBooking(true);
    try {
      console.log('🔍 [fetchAndPrefillBooking] Starting fetch for token:', token);
      const response = await apiClient.get(`/bookings/${token}`);
      console.log('🔍 [fetchAndPrefillBooking] Response received:', response.data);
      
      if (response?.success && response?.data) {
        const booking = response.data;
        const legTokens = legTokensOverride ?? changeLegs;
        const partialCtx = getPartialChangeContext(booking.flights, legTokens);
        const isPartialReturn =
          booking.route_type === 'return' && partialCtx.isPartialChange;
        console.log('📋 [fetchAndPrefillBooking] Booking data:', {
          route_type: booking.route_type,
          is_multi_leg: booking.is_multi_leg,
          is_flight_changed: booking.is_flight_changed,
          flights_count: booking.flights?.length,
          passenger_count: booking.passenger_count
        });

        const searchParams = new URLSearchParams(location.search);
        const pjoRebook = isPjoRejectionRebookFlow(booking, searchParams);

        if (booking.is_flight_changed && !pjoRebook) {
          toast.error('This booking has already been changed once.');
          navigate('/jetsetter/profile/booking-history');
          return;
        }

        if (
          !pjoRebook &&
          (booking.can_change_or_cancel === false ||
            booking.acceptance_status === 'PendingPJOConfirmation')
        ) {
          toast.error('This flight is awaiting airliner acceptance and cannot be changed yet.');
          navigate('/jetsetter/profile/booking-history');
          return;
        }

        setOriginalRouteType(booking.route_type === 'multi_city' ? 'multi_city' : booking.route_type === 'return' ? 'return' : 'one_way');
        if (booking.flights?.length) {
          setOriginalBookingFlights(booking.flights);
        }

        // Prefill form based on booking type
        if (booking.route_type === 'return' && booking.is_multi_leg && booking.flights?.length >= 2) {
          console.log('✅ [fetchAndPrefillBooking] Processing RETURN booking');
          setTripType('Returns');  // NOTE: Radio button value is 'Returns' not 'Return'
          
          const firstFlight = booking.flights[0];
          const lastFlight = booking.flights[booking.flights.length - 1];
          
          console.log('📍 First flight:', { 
            id: firstFlight.departure?.id, 
            iata: firstFlight.departure?.iata, 
            datetime: firstFlight.departure?.datetime 
          });
          console.log('📍 Last flight:', { 
            id: lastFlight.arrival?.id, 
            iata: lastFlight.arrival?.iata, 
            datetime: lastFlight.departure?.datetime 
          });
          
          // Use airport IDs directly from booking response
          const fromAirportId = firstFlight.departure?.id?.toString() || '';
          const toAirportId = firstFlight.arrival?.id?.toString() || '';
          
          console.log('🆔 Airport IDs:', { fromAirportId, toAirportId });
          
          setFromAirport(fromAirportId);
          setToAirport(toAirportId);
          setPassengers((booking.passenger_count || 1).toString());
          
          // Format dates for input (YYYY-MM-DD)
          const depDate = new Date(firstFlight.departure?.datetime);
          const retDate = new Date(lastFlight.departure?.datetime);
          const formattedDepDate = depDate.toISOString().split('T')[0];
          const formattedRetDate = retDate.toISOString().split('T')[0];
          
          console.log('📅 Dates:', { depDate: formattedDepDate, retDate: formattedRetDate });
          
          setDepartureDate(formattedDepDate);
          setReturnDate(formattedRetDate);

          if (isPartialReturn) {
            if (partialCtx.changingReturn && !partialCtx.changingOutbound) {
              toast.success('Return leg only — outbound route and date are locked.');
            } else if (partialCtx.changingOutbound && !partialCtx.changingReturn) {
              toast.success('Outbound leg only — return date is locked.');
            }
          } else {
            toast.success('Both legs selected — airports and dates can be changed. Passenger count is locked.');
          }
        } else if (booking.route_type === 'multi_city' && booking.is_multi_leg && booking.flights?.length >= 2) {
          console.log('✅ [fetchAndPrefillBooking] Processing MULTI-CITY booking');
          setTripType('Multicity');
          setPassengers((booking.passenger_count || 1).toString());
          
          // Prefill multicity segments from flights
          const segments = booking.flights.map((flight, idx) => ({
            id: idx + 1,
            from: flight.departure?.id?.toString() || '',
            to: flight.arrival?.id?.toString() || '',
            date: new Date(flight.departure?.datetime).toISOString().split('T')[0]
          }));
          
          console.log('📍 Multicity segments:', segments);
          setMulticitySegments(segments);
          
          if (partialCtx.isPartialChange) {
            toast.success(
              `Only ${partialCtx.editableSegmentIndices.length} selected leg(s) can be changed — other segments are locked.`
            );
          } else {
            toast.success('Multi-city booking loaded. Passenger count is locked.');
          }
        } else {
          // One-way
          console.log('✅ [fetchAndPrefillBooking] Processing ONE-WAY booking');
          setTripType('One Way');
          
          const fromAirportId = booking.departure?.id?.toString() || '';
          const toAirportId = booking.arrival?.id?.toString() || '';
          
          setFromAirport(fromAirportId);
          setToAirport(toAirportId);
          setPassengers((booking.passenger_count || 1).toString());
          
          const depDate = new Date(booking.departure?.datetime);
          const formattedDepDate = depDate.toISOString().split('T')[0];
          
          console.log('📅 Departure date:', formattedDepDate);
          setDepartureDate(formattedDepDate);
          
          toast.success('One-way booking loaded. Passenger count is locked.');
        }
        
        console.log('✅ [fetchAndPrefillBooking] Form prefilled successfully');
      } else {
        console.error('❌ [fetchAndPrefillBooking] Invalid response structure:', response.data);
        toast.error('Invalid booking data received');
      }
    } catch (error) {
      console.error('❌ [fetchAndPrefillBooking] Error:', error);
      toast.error('Failed to load booking details for rebooking');
    } finally {
      setIsLoadingBooking(false);
    }
  };

  // Clear saved passenger data when user starts a new search (only if not coming from URL params)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Only clear if this is a fresh search (no URL params)
    if (!params.toString()) {
      const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      const userId = currentUser.id;
      
      if (userId) {
        // Clear all passenger form data for current user
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(`passenger_form_data_${userId}_`)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
        
        if (keysToRemove.length > 0) {
          console.log('🧹 Cleared saved passenger data for new search');
        }
      }
    }
  }, []); // Run only once on mount

  // Restore form state from URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Only restore if there are URL parameters (coming from back navigation or change flight)
    if (params.toString()) {
      console.log('🔄 SearchFlightPage: Restoring from URL params:', params.toString());

      // Restore booking token for rebooking
      // Accept booking_token from URL (from BookingDetailsPage "Change Flight" button)
      // The backend will set user.change_flight when the flight search is performed
      const savedToken = params.get('booking_token');
      if (savedToken) {
        console.log('✅ Restoring booking token for rebooking:', savedToken);
        setBookingToken(savedToken);
      }

      const savedChangeLegs = params.get('change_legs');
      if (savedChangeLegs) {
        const legs = savedChangeLegs.split(',').map((token) => token.trim()).filter(Boolean);
        console.log('✅ Restoring change_legs for partial rebooking:', legs);
        setChangeLegs(legs);
      }

      // Restore trip type
      const savedTripType = params.get('tripType');
      if (savedTripType) {
        console.log('✅ Restoring trip type:', savedTripType);
        setTripType(savedTripType);
      }

      // Restore passengers
      const savedPassengers = params.get('passengers');
      if (savedPassengers) {
        console.log('✅ Restoring passengers:', savedPassengers);
        setPassengers(savedPassengers);
      }

      // Restore nearby airports
      const savedNearby = params.get('nearby');
      if (savedNearby === 'true') {
        console.log('✅ Restoring nearby airports');
        setAddNearbyAirports(true);
      }

      // Restore non-multicity fields
      if (savedTripType !== 'Multicity') {
        const savedFrom = params.get('from');
        const savedTo = params.get('to');
        const savedDeparture = params.get('departure');
        const savedReturn = params.get('return');

        if (savedFrom) {
          console.log('✅ Restoring from airport:', savedFrom);
          setFromAirport(savedFrom);
        }
        if (savedTo) {
          console.log('✅ Restoring to airport:', savedTo);
          setToAirport(savedTo);
        }
        if (savedDeparture) {
          console.log('✅ Restoring departure date:', savedDeparture);
          setDepartureDate(savedDeparture);
        }
        if (savedReturn) {
          console.log('✅ Restoring return date:', savedReturn);
          setReturnDate(savedReturn);
        }
      }

      // Restore multicity segments
      if (savedTripType === 'Multicity') {
        const segmentCount = parseInt(params.get('segmentCount') || '2');
        const segments = [];

        for (let i = 1; i <= segmentCount; i++) {
          const from = params.get(`from${i}`);
          const to = params.get(`to${i}`);
          const date = params.get(`date${i}`);

          if (from || to || date) {
            segments.push({
              id: i,
              from: from || '',
              to: to || '',
              date: date || ''
            });
          }
        }

        if (segments.length >= 2) {
          console.log('✅ Restoring multicity segments:', segments);
          setMulticitySegments(segments);
        }
      }
    } else {
      // No URL parameters - reset to fresh form and clear all saved booking data
      console.log('🔄 SearchFlightPage: No URL params, showing fresh form');
      
      // Clear all saved form data from previous bookings
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('passenger_form_data_') || key.startsWith('payment_address_data_') || key === 'currentBookingStep')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      console.log('🧹 Cleared all saved booking form data for fresh search');
      
      setTripType('One Way');
      setFromAirport('');
      setToAirport('');
      setPassengers('');
      setDepartureDate('');
      setReturnDate('');
      setAddNearbyAirports(false);
      setMulticitySegments([
        { id: 1, from: '', to: '', date: '' },
        { id: 2, from: '', to: '', date: '' },
      ]);
      setOriginalRouteType(null);
    }
  }, [location.search, user?.change_flight, navigate]);

  // Restore change-flight token from user profile when not in URL
  useEffect(() => {
    if (!bookingToken && user?.change_flight) {
      setBookingToken(user.change_flight);
    }
  }, [user?.change_flight, bookingToken]);

  // Fetch and prefill booking when booking token is set
  useEffect(() => {
    if (bookingToken) {
      const params = new URLSearchParams(location.search);
      const legs = parseChangeLegTokensFromQuery(params.get('change_legs'));
      if (legs.length) {
        setChangeLegs(legs);
      }
      console.log('🔄 Fetching booking details for token:', bookingToken);
      fetchAndPrefillBooking(bookingToken, legs);
    }
  }, [bookingToken, location.search]);

  // Reset multicity segments when switching away from Multicity (but not on initial load)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasUrlParams = params.has('tripType');

    // Only reset if we're changing trip type manually, not from URL restoration
    if (tripType !== 'Multicity' && !hasUrlParams) {
      // Reset to initial 2 segments when not in Multicity mode
      setMulticitySegments([
        { id: 1, from: '', to: '', date: '' },
        { id: 2, from: '', to: '', date: '' },
      ]);
    }
  }, [tripType, location.search]);

  // Clear URL params and form when trip type changes
  const handleTripTypeChange = (newTripType) => {
    // Clear URL parameters but preserve booking_token if exists
    if (bookingToken) {
      navigate(`/search-flight?booking_token=${bookingToken}`, { replace: true });
    } else {
      navigate('/search-flight', { replace: true });
    }

    // Update trip type
    setTripType(newTripType);

    // Clear all form fields
    setFromAirport('');
    setToAirport('');
    setPassengers('');
    setDepartureDate('');
    setReturnDate('');
    setAddNearbyAirports(false);
    setErrors({});

    // Reset multicity segments
    setMulticitySegments([
      { id: 1, from: '', to: '', date: '' },
      { id: 2, from: '', to: '', date: '' },
    ]);
  };

  // Add a new multi-city segment (max 5)
  const addMulticitySegment = () => {
    if (multicitySegments.length < 5) {
      setMulticitySegments([
        ...multicitySegments,
        { id: multicitySegments.length + 1, from: '', to: '', date: '' }
      ]);
    }
  };

  // Remove a multi-city segment (min 2)
  const removeMulticitySegment = (id) => {
    if (multicitySegments.length > 2) {
      setMulticitySegments(multicitySegments.filter(seg => seg.id !== id));
    }
  };

  // Update a specific segment
  const updateMulticitySegment = (id, field, value) => {
    setMulticitySegments(multicitySegments.map(seg =>
      seg.id === id ? { ...seg, [field]: value } : seg
    ));
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};

    // For non-multicity, validate main fields
    if (tripType !== 'Multicity') {
      if (!fromAirport) {
        newErrors.fromAirport = 'The Departure Airport field is required';
      }

      if (!toAirport) {
        newErrors.toAirport = 'The Arrival Airport field is required';
      }

      // Validate that departure and arrival airports are different
      if (fromAirport && toAirport && fromAirport === toAirport) {
        newErrors.toAirport = 'Departure and arrival airports must be different';
      }

      if (!departureDate) {
        newErrors.departureDate = 'The Departure Date Time field is required';
      }

      if (tripType === 'Returns' && !returnDate) {
        newErrors.returnDate = 'The Return Date Time field is required';
      }
    }

    // Validate passengers (required for all trip types)
    if (!passengers) {
      newErrors.passengers = 'The Passengers field is required';
    }

    // Validate multi-city segments
    if (tripType === 'Multicity') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      multicitySegments.forEach((segment, index) => {
        // Required fields
        if (!segment.from) {
          newErrors[`multicity_from_${segment.id}`] = `Segment ${index + 1}: Departure airport is required`;
        }
        if (!segment.to) {
          newErrors[`multicity_to_${segment.id}`] = `Segment ${index + 1}: Arrival airport is required`;
        }
        if (!segment.date) {
          newErrors[`multicity_date_${segment.id}`] = `Segment ${index + 1}: Departure date is required`;
        }

        // Same airport validation
        if (segment.from && segment.to && segment.from === segment.to) {
          newErrors[`multicity_route_${segment.id}`] = `Segment ${index + 1}: Departure and arrival airports must be different`;
        }

        // Future date validation
        if (segment.date) {
          const segmentDate = new Date(segment.date);
          if (segmentDate < today) {
            newErrors[`multicity_date_${segment.id}`] = `Segment ${index + 1}: Date must be in the future`;
          }
        }

        // Chronological order validation
        if (index > 0 && segment.date && multicitySegments[index - 1].date) {
          const currentDate = new Date(segment.date);
          const previousDate = new Date(multicitySegments[index - 1].date);

          if (currentDate < previousDate) {
            newErrors[`multicity_date_${segment.id}`] = `Segment ${index + 1}: Date must be on or after the previous segment's date`;
          }
        }
      });
    }

    // Set errors if any
    if (Object.keys(newErrors).length > 0) {
      console.log('❌ SearchFlightPage: Validation errors:', newErrors);
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    setIsSearching(true);

    try {
      // Prepare search parameters for API
      let searchData = {
        tripType: tripType,
        passengers: parseInt(passengers),
        nearby: addNearbyAirports,
        booking_token: bookingToken,
        change_legs: changeLegs.length > 0 ? changeLegs : undefined,
      };

      // For non-multicity trips, add main fields
      if (tripType !== 'Multicity') {
        searchData = {
          ...searchData,
          from: fromAirport,
          to: toAirport,
          departure: departureDate
        };

        if (tripType === 'Returns' && returnDate) {
          searchData.return = returnDate;
        }
      }

      // For multicity, add all segments
      if (tripType === 'Multicity') {
        searchData.segments = multicitySegments.map((seg, index) => ({
          from: seg.from,
          to: seg.to,
          date: seg.date,
          segmentNumber: index + 1
        }));
        console.log('🔍 SearchFlightPage: Multi-city segments:', searchData.segments);
      }

      console.log('🔍 SearchFlightPage: Preparing to call searchFlights with data:', searchData);
      console.log('🔍 SearchFlightPage: bookingToken state is:', bookingToken);

      // Perform the search
      const results = await searchFlights(searchData);

      console.log('✅ SearchFlightPage: Got results:', results);

      // Build URL parameters for navigation
      let urlParams = new URLSearchParams({
        passengers: passengers,
        tripType: tripType
      });

      if (bookingToken) {
        urlParams.append('booking_token', bookingToken);
      }
      if (changeLegs.length > 0) {
        urlParams.append('change_legs', changeLegs.join(','));
      }

      // For non-multicity, add main fields
      if (tripType !== 'Multicity') {
        urlParams.append('from', fromAirport);
        urlParams.append('to', toAirport);
        urlParams.append('departure', departureDate);

        if (tripType === 'Returns' && returnDate) {
          urlParams.append('return', returnDate);
        }
      }

      if (addNearbyAirports) {
        urlParams.append('nearby', 'true');
      }

      // Add multi-city segments to URL
      if (tripType === 'Multicity') {
        multicitySegments.forEach((seg, index) => {
          urlParams.append(`from${index + 1}`, seg.from);
          urlParams.append(`to${index + 1}`, seg.to);
          urlParams.append(`date${index + 1}`, seg.date);
        });
        urlParams.append('segmentCount', multicitySegments.length.toString());
      }

      // Save search parameters to sessionStorage for back navigation
      sessionStorage.setItem('flight-search-params', urlParams.toString());

      // Update the current URL with search parameters before navigating away
      // This ensures browser back button returns to search page with parameters
      window.history.replaceState(null, '', `/search-flight?${urlParams.toString()}`);

      // Navigate based on trip type with search results
      const routeMap = {
        'One Way': '/one-way-flight',
        'Returns': '/return-flight',
        'Multicity': '/multi-city-search'
      };

      const targetRoute = routeMap[tripType] || '/one-way-flight';

      console.log('🔍 SearchFlightPage: Navigating to:', targetRoute);
      navigate(`${targetRoute}?${urlParams.toString()}`, {
        state: {
          searchResults: results,
          searchParams: searchData,
          fromSearch: true
        }
      });

    } catch (error) {
      // Error is handled by the hook, but we can show additional feedback
      console.error('SearchFlightPage: Search error:', error);
      toast.error('Failed to search flights. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const dropdownArrowStyle = {
    backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")",
    backgroundPosition: 'right 16px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px'
  };

  return (
    <div className="bg-jet-gray-100 pt-20 md:pt-24 pb-0">
      <SEOHead
        title="Search Flights"
        description="Search private jet empty-leg and charter flights on JetScanner360. Compare routes, dates, and seats."
        keywords="search flights, private jet, empty leg, charter, JetScanner360"
        type="website"
      />
      <div className="w-[86%] mx-auto pt-8 pb-1">
        <div className="bg-white rounded-lg shadow-jet p-4 md:p-6 border border-jet-border">
          {/* Trip Type Selection */}
          <div className="flex items-center gap-8 mb-8 pb-6 border-b border-jet-border flex-wrap">
            <label className={`flex items-center gap-2 cursor-pointer text-sm transition-all duration-200 select-none ${tripType === 'Returns' ? 'text-jet-primary font-medium' : 'text-jet-primary'}`}>
              <input
                type="radio"
                name="tripType"
                value="Returns"
                checked={tripType === 'Returns'}
                onChange={(e) => handleTripTypeChange(e.target.value)}
                className="hidden"
              />
              <span className={`w-4 h-4 border-2 rounded-full relative transition-all duration-200 flex-shrink-0 ${tripType === 'Returns' ? 'border-jet-yellow bg-jet-yellow' : 'border-jet-border'}`}>
                {tripType === 'Returns' && <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-jet-primary rounded-full"></span>}
              </span>
              Returns
            </label>

            <label className={`flex items-center gap-2 cursor-pointer text-sm transition-all duration-200 select-none ${tripType === 'One Way' ? 'text-jet-primary font-medium' : 'text-jet-primary'} ${lockTripTypeSwitch ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="radio"
                name="tripType"
                value="One Way"
                checked={tripType === 'One Way'}
                onChange={(e) => handleTripTypeChange(e.target.value)}
                disabled={lockTripTypeSwitch}
                className="hidden"
              />
              <span className={`w-4 h-4 border-2 rounded-full relative transition-all duration-200 flex-shrink-0 ${tripType === 'One Way' ? 'border-jet-yellow bg-jet-yellow' : 'border-jet-border'}`}>
                {tripType === 'One Way' && <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-jet-primary rounded-full"></span>}
              </span>
              One Way
            </label>

            <label className={`flex items-center gap-2 cursor-pointer text-sm transition-all duration-200 select-none ${tripType === 'Multicity' ? 'text-jet-primary font-medium' : 'text-jet-primary'} ${lockTripTypeSwitch ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="radio"
                name="tripType"
                value="Multicity"
                checked={tripType === 'Multicity'}
                onChange={(e) => handleTripTypeChange(e.target.value)}
                disabled={lockTripTypeSwitch}
                className="hidden"
              />
              <span className={`w-4 h-4 border-2 rounded-full relative transition-all duration-200 flex-shrink-0 ${tripType === 'Multicity' ? 'border-jet-yellow bg-jet-yellow' : 'border-jet-border'}`}>
                {tripType === 'Multicity' && <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-jet-primary rounded-full"></span>}
              </span>
              Multicity
            </label>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="w-full">
            <div className="w-full">
              {/* One Way and Returns Form */}
              {tripType !== 'Multicity' && (
                <div className={`grid border border-dashed border-black gap-6 mb-6 rounded-lg bg-jet-gray-50 p-4 ${tripType === 'Returns' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
                  <AirportSelect
                    value={fromAirport}
                    onChange={(airportId, airport) => {
                      setFromAirport(airportId);
                      if (errors.fromAirport) {
                        setErrors({ ...errors, fromAirport: '' });
                      }
                    }}
                    placeholder="Select Departure Airport"
                    error={errors.fromAirport}
                    onClearError={() => setErrors({ ...errors, fromAirport: '' })}
                    icon={departureIcon}
                    disabled={lockAirports}
                  />

                  <AirportSelect
                    value={toAirport}
                    onChange={(airportId, airport) => {
                      setToAirport(airportId);
                      if (errors.toAirport) {
                        setErrors({ ...errors, toAirport: '' });
                      }
                    }}
                    placeholder="Select Arrival Airport"
                    error={errors.toAirport}
                    onClearError={() => setErrors({ ...errors, toAirport: '' })}
                    icon={arrivalIcon}
                    disabled={lockAirports}
                  />

                  <div className="relative w-full">
                    <div className="relative flex items-center w-full">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                        <img 
                          src={passengersIcon} 
                          alt="passengers" 
                          style={{
                            cursor: 'pointer',
                            lineHeight: '33px',
                            textAlign: 'right',
                            width: '35px',
                            color: '#312d65',
                            zIndex: 9
                          }}
                        />
                      </div>
                      <input
                        className={`w-full h-14 pl-16 pr-12 rounded-lg text-sm text-jet-gray-700 bg-white transition-all duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_3px_rgba(255,215,0,0.1)] appearance-none cursor-pointer ${lockPassengers ? 'opacity-60 cursor-not-allowed' : ''}`}
                        value={passengers}
                        type='number'
                        min="1"
                        placeholder="Number of passengers"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || parseInt(value) >= 1) {
                            setPassengers(value);
                          }
                          if (errors.passengers) {
                            setErrors({ ...errors, passengers: '' });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                            e.preventDefault();
                          }
                        }}
                        disabled={lockPassengers}
                        style={dropdownArrowStyle}
                      />
                    </div>
                    {errors.passengers && (
                      <p className="text-red-500 text-xs mt-1 ml-1">{errors.passengers}</p>
                    )}
                  </div>

                  <div className="relative w-full">
                    <div className="relative flex items-center w-full">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                        <img 
                          src={dateTimeIcon} 
                          alt="departure date" 
                          style={{
                            cursor: 'pointer',
                            lineHeight: '33px',
                            textAlign: 'right',
                            width: '35px',
                            color: '#312d65',
                            zIndex: 9
                          }}
                        />
                      </div>
                      <input
                        type="text"
                        className={`w-full h-14 pl-16 pr-4 rounded-lg text-sm text-jet-gray-700 bg-white transition-all duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_3px_rgba(255,215,0,0.1)] placeholder-jet-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 ${lockDepartureDate ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''}`}
                        placeholder="Departure"
                        value={departureDate}
                        disabled={lockDepartureDate}
                        onChange={(e) => {
                          if (lockDepartureDate) return;
                          setDepartureDate(e.target.value);
                          if (errors.departureDate) {
                            setErrors({ ...errors, departureDate: '' });
                          }
                        }}
                        onFocus={(e) => {
                          if (lockDepartureDate) return;
                          e.target.type = 'date'; 
                          e.target.min = new Date().toISOString().split('T')[0]; 
                          if (e.target.showPicker) {
                            try { e.target.showPicker(); } catch (err) {}
                          }
                        }}
                        onBlur={(e) => e.target.type = 'text'}
                        onClick={(e) => {
                          if (lockDepartureDate) return;
                          if (e.target.showPicker) {
                            try { e.target.showPicker(); } catch (err) {}
                          }
                        }}
                      />
                    </div>
                    {errors.departureDate && (
                      <p className="text-red-500 text-xs mt-1 ml-1">{errors.departureDate}</p>
                    )}
                  </div>

                  {tripType === 'Returns' && (
                    <div className="relative w-full">
                      <div className="relative flex items-center w-full">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                          <img 
                            src={dateTimeIcon} 
                            alt="return date" 
                            style={{
                              cursor: 'pointer',
                              lineHeight: '33px',
                              textAlign: 'right',
                              width: '35px',
                              color: '#312d65',
                              zIndex: 9
                            }}
                          />
                        </div>
                        <input
                          type="text"
                          className={`w-full h-14 pl-16 pr-4 rounded-lg text-sm text-jet-gray-700 bg-white transition-all duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_3px_rgba(255,215,0,0.1)] placeholder-jet-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-moz-calendar-picker-indicator]:hidden ${lockReturnDate ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''}`}
                          placeholder="Return"
                          value={returnDate}
                          disabled={lockReturnDate}
                          onChange={(e) => {
                            if (lockReturnDate) return;
                            setReturnDate(e.target.value);
                            if (errors.returnDate) {
                              setErrors({ ...errors, returnDate: '' });
                            }
                          }}
                          onFocus={(e) => {
                            if (lockReturnDate) return;
                            e.target.type = 'date'; 
                            e.target.min = departureDate || new Date().toISOString().split('T')[0]; 
                            if (e.target.showPicker) {
                              try { e.target.showPicker(); } catch (err) {}
                            }
                          }}
                          onBlur={(e) => e.target.type = 'text'}
                          onClick={(e) => {
                            if (lockReturnDate) return;
                            if (e.target.showPicker) {
                              try { e.target.showPicker(); } catch (err) {}
                            }
                          }}
                        />
                      </div>
                      {errors.returnDate && (
                        <p className="text-red-500 text-xs mt-1 ml-1">{errors.returnDate}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Multicity Flight Segments */}
              {tripType === 'Multicity' && (
                <div className="space-y-3 mb-6 border border-dashed border-black p-4 rounded-md">                  {multicitySegments.map((segment, index) => (
                  <div key={segment.id} className={`grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white p-0 lg:grid-cols-4 ${isMulticitySegmentLocked(index) ? 'opacity-80' : ''}`}>
                    {/* Departure Airport */}
                    <AirportSelect
                      value={segment.from}
                      onChange={(airportId) => {
                        updateMulticitySegment(segment.id, 'from', airportId);
                        if (errors[`multicity_from_${segment.id}`]) {
                          const newErrors = { ...errors };
                          delete newErrors[`multicity_from_${segment.id}`];
                          setErrors(newErrors);
                        }
                      }}
                      placeholder="Select Airport"
                      error={errors[`multicity_from_${segment.id}`]}
                      onClearError={() => {
                        const newErrors = { ...errors };
                        delete newErrors[`multicity_from_${segment.id}`];
                        setErrors(newErrors);
                      }}
                      icon={departureIcon}
                      disabled={isMulticitySegmentLocked(index)}
                    />

                    {/* Arrival Airport */}
                    <AirportSelect
                      value={segment.to}
                      onChange={(airportId) => {
                        updateMulticitySegment(segment.id, 'to', airportId);
                        if (errors[`multicity_to_${segment.id}`]) {
                          const newErrors = { ...errors };
                          delete newErrors[`multicity_to_${segment.id}`];
                          setErrors(newErrors);
                        }
                      }}
                      placeholder="Select Airport"
                      error={errors[`multicity_to_${segment.id}`]}
                      onClearError={() => {
                        const newErrors = { ...errors };
                        delete newErrors[`multicity_to_${segment.id}`];
                        setErrors(newErrors);
                      }}
                      icon={arrivalIcon}
                      disabled={isMulticitySegmentLocked(index)}
                    />

                    {/* Passengers - ONLY on first row */}
                    {index === 0 && (
                      <div className="relative w-full">
                        <div className="relative flex items-center w-full">
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                            <img 
                              src={passengersIcon} 
                              alt="passengers" 
                              style={{
                                cursor: 'pointer',
                                lineHeight: '33px',
                                textAlign: 'right',
                                width: '35px',
                                color: '#312d65',
                                zIndex: 9,
                                opacity: lockPassengers ? 0.5 : 1
                              }}
                            />
                          </div>
                          <input
                            disabled={lockPassengers}
                            className={`w-full h-14 pl-16 pr-4 rounded-lg text-sm text-jet-gray-700 bg-white border border-gray-300 transition-all duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_3px_rgba(255,215,0,0.1)] ${lockPassengers ? 'opacity-60 cursor-not-allowed' : ''}`}
                            value={passengers}
                            type='number'
                            min="1"
                            placeholder="Passengers"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || parseInt(value) >= 1) {
                                setPassengers(value);
                              }
                              if (errors.passengers) {
                                setErrors({ ...errors, passengers: '' });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                        {errors.passengers && (
                          <p className="text-red-500 text-xs mt-1 ml-1">{errors.passengers}</p>
                        )}
                      </div>
                    )}

                    {/* Departure Date */}
                    <div className="relative w-full">
                      <div className="relative flex items-center w-full">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                          <img 
                            src={dateTimeIcon} 
                            alt="departure date" 
                            style={{
                              cursor: 'pointer',
                              lineHeight: '33px',
                              textAlign: 'right',
                              width: '35px',
                              color: '#312d65',
                              zIndex: 9
                            }}
                          />
                        </div>
                        <input
                          type="text"
                          className={`w-full h-14 pl-16 pr-4 rounded-lg text-sm text-jet-gray-700 bg-white border border-gray-300 transition-all duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_3px_rgba(255,215,0,0.1)] placeholder-jet-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-moz-calendar-picker-indicator]:hidden ${isMulticitySegmentLocked(index) ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''}`}
                          placeholder="Departure"
                          value={segment.date}
                          disabled={isMulticitySegmentLocked(index)}
                          onChange={(e) => {
                            if (isMulticitySegmentLocked(index)) return;
                            updateMulticitySegment(segment.id, 'date', e.target.value);
                            if (errors[`multicity_date_${segment.id}`]) {
                              const newErrors = { ...errors };
                              delete newErrors[`multicity_date_${segment.id}`];
                              setErrors(newErrors);
                            }
                          }}
                          onFocus={(e) => {
                            if (isMulticitySegmentLocked(index)) return;
                            e.target.type = 'date'; 
                            e.target.min = new Date().toISOString().split('T')[0]; 
                            if (e.target.showPicker) {
                              try { e.target.showPicker(); } catch (err) {}
                            }
                          }}
                          onBlur={(e) => e.target.type = 'text'}
                          onClick={(e) => {
                            if (isMulticitySegmentLocked(index)) return;
                            if (e.target.showPicker) {
                              try { e.target.showPicker(); } catch (err) {}
                            }
                          }}
                        />
                      </div>
                      {errors[`multicity_date_${segment.id}`] && (
                        <p className="text-red-500 text-xs mt-1 ml-1">{errors[`multicity_date_${segment.id}`]}</p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {/* Show Add City button on last segment if less than 5 */}
                      {index === 1 ? (
                        <button
                          type="button"
                          onClick={addMulticitySegment}
                          className="text-black bg-[#ffd200] border-0 flex-1 px-6 py-4 rounded-lg text-md font-bold cursor-pointer transition-all duration-200 whitespace-nowrap h-14 hover:bg-[#ffb300] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
                        >
                          <i className="fas fa-plus mr-2"></i>
                          Add City
                        </button>
                      ) : (multicitySegments.length > 2 && index !== 0) ? (
                        <button
                          type="button"
                          onClick={() => removeMulticitySegment(segment.id)}
                          className="text-black bg-[#ffd200] border-0 flex-1 px-6 py-4 rounded-lg text-md font-bold cursor-pointer transition-all duration-200 h-14 hover:bg-[#ffb300] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
                        >
                          <i className="fas fa-minus mr-2"></i>
                          Remove City
                        </button>
                      ) : (
                        <div className="flex-1"></div>
                      )}
                    </div>
                  </div>
                ))}
                </div>
              )}

              {/* Add Nearby Airports Checkbox - Only show for Returns and One Way */}
              {tripType !== 'Multicity' && (
                <div className="mb-8">
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-jet-gray-700 select-none">
                    <input
                      type="checkbox"
                      checked={addNearbyAirports}
                      onChange={(e) => setAddNearbyAirports(e.target.checked)}
                      className="hidden"
                    />
                    <span className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 flex-shrink-0 ${addNearbyAirports ? 'bg-jet-yellow border-jet-yellow' : 'border-jet-gray-300'}`}>
                      {addNearbyAirports && <span className="text-jet-gray-800 text-xs font-bold">✓</span>}
                    </span>
                    Add Nearby Airports
                  </label>
                </div>
              )}

              {/* Search Button */}
              <div className="flex justify-end pr-8">
                <button
                  type="submit"
                  disabled={isSearching}
                  className="bg-jet-yellow text-jet-gray-800 border-0 rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 text-[14px] h-[36px] w-[96px] hover:bg-jet-yellow-hover hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(255,215,0,0.3)] focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,215,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SearchFlightPage;